import { Outlet } from "react-router-dom"

import { useRbac } from "@/lib/auth"
import { SeccionTabs } from "@/components/SeccionTabs"
import { ComentariosDeLaVista } from "@/components/Comentarios"

/**
 * Contabilidad: todo lo que se opera, no lo que se analiza.
 *
 * Las cuatro vistas comparten una idea —se ENTRA a corregir— y por eso van
 * juntas: la cola de revision aprueba y corrige clasificaciones, los maestros se
 * editan con motivo, las reglas de tratamiento se reemplazan versionadas y las
 * declaraciones se firman. El Dashboard, en cambio, no deja tocar nada.
 *
 * `Declaraciones` aparece para quien tiene cartera (cross-tenant) Y para quien
 * puede FIRMAR en este cliente. Estaba gateada sólo por «¿perteneces a más de un
 * cliente?», que es una pregunta sobre cuántos clientes llevas y no sobre qué
 * haces: el contador interno de una empresa con un solo cliente no podía llegar
 * a declarar ni a firmar desde la pantalla, y un dueño con dos clientes sí.
 */
export function ContabilidadLayout() {
  const { isCrossTenant, can } = useRbac()
  const declara = isCrossTenant || can("sign")

  const vistas = [
    { to: "revision", label: "Revisión" },
    { to: "libros", label: "Libros" },
    // Los libros son el material de trabajo; los estados son el entregable. Van
    // seguidos y en ese orden porque uno sale del otro.
    { to: "estados", label: "Estados contables" },
    /*
      PLANILLA. Punto 10 de Cesar. Va en Contabilidad y no en Finanzas porque no
      es una vista del modelo de gestión: es lo que se DECLARA y lo que se paga,
      y sale del maestro de personas con la norma de cada país.

      Finanzas ya tiene un «Payroll» y son cosas distintas: aquél es el costo de
      equipo dentro del P&L de gestión; éste es la planilla del mes con sus
      aportes, sus retenciones y la fecha en que cada concepto sale de caja.
    */
    { to: "planilla", label: "Planilla" },
    { to: "maestros", label: "Maestros" },
    ...(declara ? [{ to: "declaraciones", label: "Declaraciones" }] : []),
    /*
      EL EXPEDIENTE NO VA GATEADO, y las declaraciones sí. Es la decisión que lo
      hace útil (punto 9 de la cuarta tanda): quien necesita la ficha RUC para
      dar de alta a un cliente es comercial o administración, y si la pestaña
      sólo la vieran los contadores habría que seguir pidiéndosela a alguien —
      que es el paso que esto viene a quitar. El backend lo respalda: listar y
      descargar bastan con permiso de lectura.
    */
    { to: "expediente", label: "Expediente" },
  ]

  return (
    <div className="space-y-3">
      <SeccionTabs tabs={vistas} ariaLabel="Vistas de contabilidad"
                   derecha={<ComentariosDeLaVista seccion="Contabilidad" vistas={vistas} />} />

      <Outlet />
    </div>
  )
}
